import { Component, OnInit, OnDestroy, Inject, ViewChild, AfterViewInit } from '@angular/core';

import { AngularFirestore, AngularFirestoreCollection } from '@angular/fire/compat/firestore';
import { Observable, Subscription } from 'rxjs';

import { SelectionModel } from '@angular/cdk/collections';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { MatSelect } from '@angular/material/select';

import { ConfigurationService } from '../configuration.service';
import { UploadedFile } from '../firestore-models/uploaded-file';
import { UploadedFilesAttribute } from '../firestore-models/uploaded-files-attribute';
import { ApiUploadedFile} from '../api-client-module';
import { AppConst } from '../app-const';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-select-uploaded-file-dialog',
  templateUrl: './select-uploaded-file-dialog.component.html',
  styleUrls: ['./select-uploaded-file-dialog.component.css'],
  standalone: false,
})
export class SelectUploadedFileDialogComponent implements OnInit, OnDestroy, AfterViewInit {

  private uploadedFilesCollection!: AngularFirestoreCollection<UploadedFile>;
  uploadedFilesObservable!: Observable<UploadedFile[]>;
  uploadedFilesSubscribe!: Subscription;

  private uploadedFilesAttributesCollection!: AngularFirestoreCollection<UploadedFilesAttribute>;
  uploadedFilesAttributesObservable!: Observable<UploadedFilesAttribute[]>;
  uploadedFilesAttributesSubscribe!: Subscription;
  uploadedFilesAttributes: UploadedFilesAttribute[] = [];

  projectId: string;

  displayedColumns: string[] = ['position', 'select', 'file', 'name', 'attributes', 'date', 'download'];
  dataSource = new MatTableDataSource<UploadedFile>();
  selection = new SelectionModel<UploadedFile>(true, []);

  currentLang: string = 'ja';

  isHovering: boolean = false;
  files: File[] = [];
  compless: string = "1";
  selectedAttributeId: string = '';

  allUploadedFiles: UploadedFile[] = [];

  @ViewChild(MatPaginator, {static: false}) paginator!: MatPaginator;
  @ViewChild(MatSort, { static: false }) sort!: MatSort;
  @ViewChild('attributeSelect') attributeSelect!: MatSelect;

  constructor(
    public dialogRef: MatDialogRef<SelectUploadedFileDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private angularFirestore: AngularFirestore,
    private configurationService: ConfigurationService,
    private translate: TranslateService
  ) {
    this.projectId = this.configurationService.getProjectId();
  }

  ngOnInit(): void {
    this.uploadedFilesCollection = this.angularFirestore.collection<UploadedFile>(
      AppConst.FIRESTORE_UPLOADED_FILES, ref => ref.where('project_id', '==', this.projectId).orderBy('created', 'desc'));
    this.uploadedFilesObservable = this.uploadedFilesCollection.valueChanges({ idField: 'id' });
    this.uploadedFilesSubscribe = this.uploadedFilesObservable.subscribe(uploadedFiles => {
      let data = [];
      for (const uploadedFile of uploadedFiles) {
        if (this.data.type && this.data.type == 'DL') {
          if (this.isImage(uploadedFile) && uploadedFile.type == 'DL') {
            data.push(uploadedFile);
          }
        } else {
          if (this.isImage(uploadedFile) && uploadedFile.type != 'DL') {
            data.push(uploadedFile);
          }
        }
      }
      this.allUploadedFiles = data;
      this.applyAttributeFilter();
    });

    // アップロードファイル属性を読み込む
    this.uploadedFilesAttributesCollection = this.angularFirestore.collection<UploadedFilesAttribute>(
      AppConst.FIRESTORE_UPLOADED_FILES_ATTR, ref => ref.where('project_id', '==', this.projectId)
        .orderBy('attribute_id', 'asc'));
    this.uploadedFilesAttributesObservable = this.uploadedFilesAttributesCollection.valueChanges({ idField: 'id' });
    this.uploadedFilesAttributesSubscribe = this.uploadedFilesAttributesObservable.subscribe(attributes => {
      // enabled=trueのものだけをフィルタ
      this.uploadedFilesAttributes = attributes.filter(attr => attr.enabled);
      // 属性データが更新されたら、既存のファイルのフィルター値も更新
      if (this.allUploadedFiles.length > 0) {
        this.applyAttributeFilter();
      }
    });

    this.currentLang = this.translate.currentLang;
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;

    // カスタムフィルタープレディケートを設定（filterValueでフィルタリング）
    this.dataSource.filterPredicate = (data: any, filter: string) => {
      return data.filterValue ? data.filterValue.toLowerCase().includes(filter) : false;
    };
  }

  ngOnDestroy(): void {
    this.uploadedFilesSubscribe.unsubscribe();
    this.uploadedFilesAttributesSubscribe.unsubscribe();
  }

  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected === numRows;
  }

  masterToggle() {
    this.isAllSelected() ?
    this.selection.clear() :
    this.dataSource.data.forEach(row => this.selection.select(row));
  }

  checkboxLabel(row?: UploadedFile): string {
    if (!row) {
      return `${this.isAllSelected() ? 'select' : 'deselect'} all`;
    }
    return `${this.selection.isSelected(row) ? 'deselect' : 'select'}`;
  }

  selectFiles() {
    let uploadedFiles: Array<ApiUploadedFile> = new Array<ApiUploadedFile>();
    for (const row of this.dataSource.data) {
      if (this.selection.isSelected(row)) {
        const uploadedFile: ApiUploadedFile = {
          id: row.id,
          type: row.type ? row.type : '',
          file_name: row.file_name,
          file_path: row.file_path,
          download_url: row.download_url,
          project_id: row.project_id,
          enabled: row.enabled,
          attribute_ids: row.attribute_ids
        };
        uploadedFiles.push(uploadedFile);
      }
    }
    this.dialogRef.close(uploadedFiles);
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

  isImage(uploadedFile: UploadedFile): boolean {
    // 拡張子取得
    const pos = uploadedFile.file_name.lastIndexOf('.');
    let ext = '';
    if (pos !== -1) {
      ext = uploadedFile.file_name.slice(pos + 1).toLowerCase();
    }
    if (AppConst.IMAGE_FILE_EXTS.indexOf(ext) === -1) {
      return false;
    }
    return true
  }

  sortData(sort: Sort) {
    const data = this.dataSource.data.slice();
    if (!sort.active || sort.direction === '') {
      this.dataSource.data = data;
      return;
    }
    const compare = (a: number | string, b: number | string, isAsc: boolean) => {
      return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
    }

    this.dataSource.data = data.sort((a, b) => {
      const isAsc = sort.direction === 'asc';
      switch (sort.active) {
        case 'name':
          return compare(a.file_name.substr(14), b.file_name.substr(14), isAsc);
        case 'date':
          return compare(a.created.toString(), b.created.toString(), isAsc);
        default:
          return 0;
      }
    });
  }

  applyFilter(event: Event) {
    this.selection.clear();
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();
  }

  //ホバー時のイベント発火
  toggleHover(event: boolean) {
    this.isHovering = event;
  }

  //ファイルアップロード（ドラッグ&ドロップ）
  onDrop(files: any) {
    for (let i = 0; i < files.length; i++) {
      this.files.push(files.item(i)!);
    }
  }

  //ファイルアップロード（ファイル選択）
  onChangeFileInput(event: Event) {
    const target = event.target as HTMLInputElement;
    if (target.files!.length > 0) {
      for (let i = 0; i < target.files!.length; i++) {
        this.files.push(target.files!.item(i)!);
      }
    }
  }


  complessChange(event: any) {
    this.compless = event.value;
  }

  getAttributeName(attributeId: string): string {
    const attribute = this.uploadedFilesAttributes.find(attr => attr.attribute_id === attributeId);
    if (attribute && attribute.attribute_names && attribute.attribute_names.length > 0) {
      const langName = attribute.attribute_names.find(name => name.lang_code === this.currentLang);
      if (langName) {
        return langName.name;
      }
      return attribute.attribute_names[0].name;
    }
    return '';
  }

  getAttributeNames(attributeIds?: Array<string>): string {
    if (!attributeIds || attributeIds.length === 0) {
      return '';
    }
    return attributeIds.map(id => this.getAttributeName(id)).join(', ');
  }

  getAllLanguageAttributeNames(attributeIds?: Array<string>): string {
    if (!attributeIds || attributeIds.length === 0) {
      return '';
    }
    const allNames: string[] = [];
    for (const attrId of attributeIds) {
      const attribute = this.uploadedFilesAttributes.find(attr => attr.attribute_id === attrId);
      if (attribute && attribute.attribute_names && attribute.attribute_names.length > 0) {
        // すべての言語の属性名を含める
        for (const name of attribute.attribute_names) {
          if (name.name) {
            allNames.push(name.name);
          }
        }
      }
    }
    return allNames.join(' ');
  }

  addFilterValue(files: Array<any>): Array<any> {
    for (const file of files) {
      let filterValue = file.file_name + ' ';
      if (file.attribute_ids && file.attribute_ids.length > 0) {
        // 属性IDとすべての言語の属性名を含める
        filterValue += file.attribute_ids.join(' ') + ' ' + this.getAllLanguageAttributeNames(file.attribute_ids);
      }
      file['filterValue'] = filterValue;
    }
    return files;
  }

  applyAttributeFilter(): void {
    this.dataSource.data = this.addFilterValue(this.allUploadedFiles);
  }

  onAttributeSelectionChange(event: any): void {
    // 何もしない（画像圧縮と同じ）
  }

  downloadFile(uploadedFile: UploadedFile): void {
    fetch(uploadedFile.download_url)
      .then(response => response.blob())
      .then(blob => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = uploadedFile.file_name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      })
      .catch(error => {
        console.error('Download failed:', error);
      });
  }

}
